import type { GASetupDatalayerParamPage } from '@integration-layer/schemas/GAEvents/dataLayerSetup'
import snakeCase from 'lodash/snakeCase'
import type { ProductSchema as AlgoliaProductType } from '@integration-layer/.nuxt/armani/product-schema'

const getEnglishCategoriesFromProduct = (
  hierarchicalCategories: AlgoliaProductType['hierarchicalCategories'],
  categoryPageId2: AlgoliaProductType['categoryPageId2']
): { mainCategoryEn: string } => {
  // Get the depth of the category from hierarchicalCategories key names
  const hierarchyLevel = Object.keys(hierarchicalCategories)
    .map(key => parseInt(key.replace('lvl', '').replace('0', ''))) // Extract level number
    .sort()

  const deepestLevel = Math.max(...hierarchyLevel)

  // Find the first category page id has the biggest hierarchy depth
  const targetCategoryPageId = categoryPageId2?.find(categoryId => {
    const splitCategory = categoryId.split('>')
    return splitCategory.length === deepestLevel
  })

  if (!targetCategoryPageId) {
    return { mainCategoryEn: '' }
  }

  return {
    mainCategoryEn: targetCategoryPageId?.split('>').at(-1)?.trim() || '',
  }
}

/**
 * Generates a parameter page object for Google Analytics tracking of product items.
 *
 * @param {AlgoliaProductType} algoliaProduct - The Algolia product object to extract tracking parameters from.
 * @returns {GASetupDatalayerParamPage} A structured object with page tracking parameters.
 *
 * @description This composable extracts and formats product-specific tracking information
 * for Google Analytics, including gender, department, brand, and category details.
 *
 * @example
 * // Returns a tracking parameter object for a product
 * const paramPage = useParamPageItem(productObject)
 * // Example output:
 * // {
 * //   page_type: 'item',
 * //   brand: 'armani_exchange',
 * //   department: 'IT_SIZEGRIDCODE',
 * //   gender: 'D',
 * //   page_status_code: 200,
 * //   sub_page_type: 'IT_SIZEGRIDCODE_CategoryName'
 * // }
 */
export const useParamPageItem = (algoliaProduct: AlgoliaProductType) => {
  const { country } = useRouteHelper()

  /**
   * Determines the gender category for the product.
   *
   * @returns {string} Gender code: 'D' for women, 'U' for men, 'E' for unspecified.
   * @private
   */
  const getGender = () => {
    // ideal category string is "giorgio-armani > man"
    const categoriesSplitted = algoliaProduct.categoryPageId2?.[1]
      .split('>')
      .map(c => c.trim())

    const genderExtended: 'man' | 'woman' | undefined =
      categoriesSplitted?.find((cat): cat is 'man' | 'woman' =>
        ['man', 'woman'].includes(cat.toLowerCase())
      )

    return genderExtended === 'woman'
      ? 'D'
      : genderExtended === 'man'
        ? 'U'
        : 'E'
  }
  const gender = getGender()

  // Construct department using country and size grid code
  const department = `${country.toUpperCase()}_${algoliaProduct.SizeGridCode}`

  // Convert brand to snake_case for consistent tracking
  const brand = snakeCase(algoliaProduct.Brand)

  const { mainCategoryEn } = getEnglishCategoriesFromProduct(
    algoliaProduct.hierarchicalCategories,
    algoliaProduct.categoryPageId2
  )

  // Capitalize the first letter of the main category
  const mainCategoryFirstLetterCapitalized = capitalizeFirstLetter(
    mainCategoryEn.toLowerCase()
  )

  /**
   * Constructs the parameter page object for Google Analytics tracking.
   *
   * @type {GASetupDatalayerParamPage}
   */
  const paramPage: GASetupDatalayerParamPage = {
    page_type: 'item',
    brand,
    department,
    gender,
    page_status_code: 200,
    sub_page_type: `${department}_${mainCategoryFirstLetterCapitalized}`,
    product_focus: algoliaProduct['editorial-product-focus']?.length
      ? 'Y'
      : 'N',
  }
  return paramPage
}
